/**
 * People reach the check from a separate lead form (name, mobile, email) that
 * parks what it collected in localStorage. We read whatever it left so the
 * capture step starts filled in. The form lives outside this app, so the reader
 * is deliberately tolerant about keys and field names — and equally strict
 * about the values: anything that fails the same validation the form applies is
 * dropped, so a bad value can never slip past the capture step unseen.
 */

export type LeadPrefill = { name: string; phone: string; email: string };

export const EMPTY_PREFILL: LeadPrefill = { name: "", phone: "", email: "" };

/* Storage keys the lead form is known to use, tried in order. */
const KEYS = ["miles_lead", "lead", "leadData", "lead_data", "caira_lead", "miles_lead_form"];

/* Field names seen across the lead form's own versions. */
/* `firstname` is handled separately, so it can pair with a last name. */
const NAME_FIELDS = ["name", "fullname", "full_name", "leadname", "lead_name"];
const PHONE_FIELDS = [
  "phone",
  "mobile",
  "whatsapp",
  "phonenumber",
  "phone_number",
  "mobilenumber",
  "mobile_number",
  "lead_phone",
  "contact",
];
const EMAIL_FIELDS = ["email", "emailaddress", "email_address", "email_id", "mail", "lead_email"];

export function nameError(v: string) {
  const s = v.trim();
  if (!s) return "Please enter your name.";
  if (s.length < 2) return "That looks too short.";
  if (s.length > 60) return "Please keep it under 60 characters.";
  if (!/^[\p{L}][\p{L}\s.'-]*$/u.test(s)) return "Letters, spaces and . ' - only.";
  return "";
}

export function phoneError(v: string) {
  const d = v.trim().replace(/\D/g, "");
  if (!d) return "Please enter your WhatsApp number.";
  const ten = d.length > 10 ? d.slice(-10) : d;
  if (ten.length !== 10) return "Enter a 10-digit mobile number.";
  if (!/^[6-9]/.test(ten)) return "Indian mobile numbers start with 6, 7, 8 or 9.";
  return "";
}

export function emailError(v: string) {
  const s = v.trim();
  if (!s) return "Please enter your email.";
  if (s.length > 120) return "That email is too long.";
  if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(s)) return "That email doesn't look right.";
  return "";
}

/** Digits only, last 10 — what the capture step submits. */
export function normalisePhone(v: string) {
  return v.replace(/\D/g, "").slice(-10);
}

function str(v: unknown) {
  return typeof v === "string" ? v : typeof v === "number" ? String(v) : "";
}

/** Case- and separator-insensitive lookup across a flat record. */
function pick(src: Record<string, unknown>, fields: readonly string[]) {
  for (const [k, v] of Object.entries(src)) {
    const key = k.toLowerCase().replace(/[^a-z_]/g, "");
    if (fields.includes(key) || fields.includes(key.replace(/_/g, ""))) {
      const s = str(v).trim();
      if (s) return s;
    }
  }
  return "";
}

/** One level of nesting, so `{ lead: { name } }` and `{ data: { name } }` work too. */
function flatten(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) {
        if (!(k2 in out)) out[k2] = v2;
      }
    } else {
      out[k] = v;
    }
  }
  return out;
}

function parse(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    const flat = flatten(parsed);
    return Object.keys(flat).length ? flat : null;
  } catch {
    return null;
  }
}

function fromRecord(rec: Record<string, unknown>): LeadPrefill {
  const first = pick(rec, ["firstname", "first_name"]);
  const last = pick(rec, ["lastname", "last_name"]);
  const name = pick(rec, NAME_FIELDS) || [first, last].filter(Boolean).join(" ");
  const phone = pick(rec, PHONE_FIELDS);
  const email = pick(rec, EMAIL_FIELDS);
  return { name: name.trim(), phone: phone.trim(), email: email.trim() };
}

/** Drop anything the capture step would reject, so we never prefill a bad value. */
function clean(p: LeadPrefill): LeadPrefill {
  const phone = normalisePhone(p.phone);
  return {
    name: nameError(p.name) ? "" : p.name.trim().slice(0, 60),
    phone: phoneError(phone) ? "" : phone,
    email: emailError(p.email) ? "" : p.email.trim(),
  };
}

/**
 * Read whatever the lead form left behind. Returns empty strings when there is
 * nothing usable — the capture step then behaves exactly as it does today and
 * will not let anyone through until the fields are filled.
 */
export function readLeadPrefill(
  storage?: Pick<Storage, "getItem" | "key" | "length">,
): LeadPrefill {
  const store = storage ?? (typeof localStorage === "undefined" ? null : localStorage);
  if (!store) return EMPTY_PREFILL;

  const candidates: Record<string, unknown>[] = [];
  try {
    for (const k of KEYS) {
      const rec = parse(store.getItem(k));
      if (rec) candidates.push(rec);
    }
    /* Fallback: any other lead-ish key, plus flat `lead_name` style entries. */
    const flat: Record<string, unknown> = {};
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (!k || KEYS.includes(k)) continue;
      const lower = k.toLowerCase();
      const raw = store.getItem(k);
      if (raw && /lead|enquir|inquir|contact/.test(lower)) {
        const rec = parse(raw);
        if (rec) candidates.push(rec);
        else flat[lower] = raw;
      }
    }
    if (Object.keys(flat).length) candidates.push(flat);
  } catch {
    return EMPTY_PREFILL;
  }

  /* First usable value per field wins, so an earlier key can fill what a later one lacks. */
  const merged = { ...EMPTY_PREFILL };
  for (const rec of candidates) {
    const got = clean(fromRecord(rec));
    merged.name ||= got.name;
    merged.phone ||= got.phone;
    merged.email ||= got.email;
  }
  return merged;
}
